import React from 'react';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { ShiftRegisterItem } from '@/api/models';
import { useCloseShiftByIdMutation } from '@/api/queries/shifts';

/** The open shift the admin is closing, as the register row showed it. */
export type ShiftCloseTarget = {
    id: string;
    sellerName: string | null;
    openedAt: string;
};

/** The close as the server recorded it, not as the admin typed it. */
export type ShiftCloseRecord = {
    expected: number;
    counted: number;
    difference: number;
};

export type ShiftCloseLogic = {
    /** The shift being closed; the modal is open while this is set. */
    target: ShiftCloseTarget | null;
    /** The blind count, then the recorded close. */
    step: 'count' | 'closed';
    counted: number | null;
    setCounted: (value: number | null) => void;
    /** A count is entered and no close is in flight. */
    canConfirm: boolean;
    isSubmitting: boolean;
    /** The last refusal, for the view to localize; cleared by a new count. */
    error: ErrorResponse | null;
    /** Set once the server closed the shift. */
    record: ShiftCloseRecord | null;
    onStart: (row: ShiftRegisterItem) => void;
    onConfirm: () => void;
    onDismiss: () => void;
};

/**
 * The admin's "Close shift" on an open register row (SEL-31): a blind count —
 * the admin never sees the expected cash before counting — then a close by id,
 * then the difference the server recorded. There is no co-sign: the server
 * records the admin as approver.
 *
 * A close that lands after the modal was dismissed or moved on to another shift
 * is ignored, so it can never show one shift's figures against another.
 */
export const useShiftClose = (): ShiftCloseLogic => {
    const closeMutation = useCloseShiftByIdMutation();
    const { mutate } = closeMutation;

    const [target, setTarget] = React.useState<ShiftCloseTarget | null>(null);
    const [counted, setCountedState] = React.useState<number | null>(null);
    const [error, setError] = React.useState<ErrorResponse | null>(null);
    const [record, setRecord] = React.useState<ShiftCloseRecord | null>(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const generation = React.useRef(0);

    const reset = React.useCallback((next: ShiftCloseTarget | null) => {
        generation.current += 1;
        setTarget(next);
        setCountedState(null);
        setError(null);
        setRecord(null);
        setIsSubmitting(false);
    }, []);

    const onStart = React.useCallback(
        (row: ShiftRegisterItem) => {
            if (row.status !== 'Open') {
                return;
            }
            reset({
                id: row.id,
                sellerName: row.openedByName,
                openedAt: row.openedAt,
            });
        },
        [reset],
    );

    const onDismiss = React.useCallback(() => reset(null), [reset]);

    const setCounted = React.useCallback((value: number | null) => {
        setCountedState(value);
        setError(null);
    }, []);

    const onConfirm = React.useCallback(() => {
        if (target === null || counted === null || isSubmitting) {
            return;
        }
        const current = generation.current;
        setIsSubmitting(true);
        setError(null);
        mutate(
            { shiftId: target.id, body: { countedCash: counted } },
            {
                onSuccess: (shift) => {
                    if (generation.current !== current) {
                        return;
                    }
                    setIsSubmitting(false);
                    setRecord({
                        expected: shift.expectedAtClose ?? 0,
                        counted: shift.closingCount ?? counted,
                        difference: shift.closeDiscrepancy ?? 0,
                    });
                },
                onError: (refusal) => {
                    if (generation.current !== current) {
                        return;
                    }
                    setIsSubmitting(false);
                    setError(refusal);
                },
            },
        );
    }, [target, counted, isSubmitting, mutate]);

    return {
        target,
        step: record === null ? 'count' : 'closed',
        counted,
        setCounted,
        canConfirm: counted !== null && !isSubmitting,
        isSubmitting,
        error,
        record,
        onStart,
        onConfirm,
        onDismiss,
    };
};
